import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import {
  TRACKING_SCHEMAS,
  createTrackingEvent,
} from 'dashboard/helper/AnalyticsHelper/schemas';
import {
  getSessionId,
  trafficSource,
} from 'dashboard/helper/AnalyticsHelper/utils/trackingEnvelope';
import { platformTrackingProperties } from 'dashboard/helper/lais/platform';

const safeNavigatorLanguage = () => {
  if (typeof window === 'undefined') return 'pt-BR';
  return window.navigator?.language || 'pt-BR';
};

const logTrackingValidationError = error => {
  if (!import.meta.env.DEV) return;
  // Tracking must not interrupt the operational dashboard.
  // eslint-disable-next-line no-console
  console.warn('[LAIS tracking] event skipped', error);
};

export const getBaseSchema = ({ analytics = AnalyticsHelper } = {}) => ({
  ...(analytics.defaultProperties?.() || {}),
  ...platformTrackingProperties(),
  sessionId: getSessionId(),
  locale: safeNavigatorLanguage(),
  ...trafficSource(),
});

export const resolveTrackingInput = (
  eventOrCreator,
  properties = {},
  options = {}
) => {
  if (typeof eventOrCreator === 'string') {
    return { eventName: eventOrCreator, eventProperties: properties };
  }

  if (typeof eventOrCreator?.create !== 'function') {
    throw new TypeError('useTracking.track requires an event name or creator');
  }

  const { variant: inlineVariant, ...eventData } = properties;
  const created = eventOrCreator.create(
    options.variant || inlineVariant || 'default',
    eventData
  );
  const { event, ...eventProperties } = created;
  return { eventName: event, eventProperties };
};

export function useTracking({ analytics = AnalyticsHelper } = {}) {
  const track = (eventOrCreator, properties = {}, options = {}) => {
    try {
      const { eventName, eventProperties } = resolveTrackingInput(
        eventOrCreator,
        properties,
        options
      );
      const payload = createTrackingEvent(eventName, {
        ...getBaseSchema({ analytics }),
        ...eventProperties,
      });
      return analytics.track(payload.event, payload.properties);
    } catch (error) {
      logTrackingValidationError(error);
      return null;
    }
  };

  return {
    track,
    schemas: TRACKING_SCHEMAS,
    createTrackingEvent,
    getBaseSchema: () => getBaseSchema({ analytics }),
  };
}

export default useTracking;
